import settingsService from '../services/settings.service';
import { Setting } from '../types/settings.types';

// Cache for settings
let settingsCache: Record<string, any> = {};
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get all settings from cache or API
 */
export async function getAllSettings(): Promise<Record<string, any>> {
  const now = Date.now();
  
  // Return cached settings if still valid
  if (cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
    return settingsCache;
  }
  
  try {
    const settings = await settingsService.getSettings();
    settingsCache = {};
    
    settings.forEach((setting: Setting) => {
      settingsCache[setting.key] = setting.typed_value ?? setting.value;
    });
    
    cacheTimestamp = now;
    return settingsCache;
  } catch (error) {
    console.error('Failed to load settings:', error);
    return settingsCache; // Return existing cache on error
  }
}

/**
 * Get a specific setting value
 */
export async function getSetting(key: string, defaultValue: any = null): Promise<any> {
  const settings = await getAllSettings();
  return settings[key] ?? defaultValue;
}

/**
 * Get risk level colors from settings
 */
export async function getRiskColors(): Promise<Record<string, string>> {
  const defaultColors = {
    LOW: '#4caf50',
    MEDIUM: '#ff9800',
    HIGH: '#f44336',
    CRITICAL: '#9c27b0',
  };
  
  try {
    const settings = await getAllSettings();
    return {
      LOW: settings.RISK_COLOR_LOW || defaultColors.LOW,
      MEDIUM: settings.RISK_COLOR_MEDIUM || defaultColors.MEDIUM,
      HIGH: settings.RISK_COLOR_HIGH || defaultColors.HIGH,
      CRITICAL: settings.RISK_COLOR_CRITICAL || defaultColors.CRITICAL,
    };
  } catch {
    return defaultColors;
  }
}

/**
 * Get risk score thresholds from settings
 */
export async function getRiskThresholds(): Promise<{
  low: number;
  medium: number;
  high: number;
}> {
  const defaultThresholds = {
    low: 3,
    medium: 9,
    high: 15,
  };
  
  try {
    const settings = await getAllSettings();
    return {
      low: settings.RISK_SCORE_LOW_THRESHOLD || defaultThresholds.low,
      medium: settings.RISK_SCORE_MEDIUM_THRESHOLD || defaultThresholds.medium,
      high: settings.RISK_SCORE_HIGH_THRESHOLD || defaultThresholds.high,
    };
  } catch {
    return defaultThresholds;
  }
}

/**
 * Calculate risk level based on score and settings
 */
export async function calculateRiskLevel(score: number): Promise<string> {
  const thresholds = await getRiskThresholds();
  
  if (score <= thresholds.low) return 'LOW';
  if (score <= thresholds.medium) return 'MEDIUM';
  if (score <= thresholds.high) return 'HIGH';
  return 'CRITICAL';
}

/**
 * Clear settings cache
 */
export function clearSettingsCache(): void {
  settingsCache = {};
  cacheTimestamp = 0;
}

/**
 * Get UI theme colors from settings
 */
export async function getThemeColors(): Promise<{
  primary: string;
  secondary: string;
  error: string;
  warning: string;
  info: string;
  success: string;
}> {
  const defaultColors = {
    primary: '#1976d2',
    secondary: '#dc004e',
    error: '#f44336',
    warning: '#ff9800',
    info: '#2196f3',
    success: '#4caf50',
  };
  
  try {
    const settings = await getAllSettings();
    return {
      primary: settings.UI_PRIMARY_COLOR || defaultColors.primary,
      secondary: settings.UI_SECONDARY_COLOR || defaultColors.secondary,
      error: settings.UI_ERROR_COLOR || defaultColors.error,
      warning: settings.UI_WARNING_COLOR || defaultColors.warning,
      info: settings.UI_INFO_COLOR || defaultColors.info,
      success: settings.UI_SUCCESS_COLOR || defaultColors.success,
    };
  } catch {
    return defaultColors;
  }
}

/**
 * Get public settings (accessible without authentication)
 */
export async function getPublicSettings(): Promise<Record<string, any>> {
  try {
    const response = await fetch('/api/public/settings/', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch public settings');
    }
    
    const data = await response.json();
    // Transform the response to a simpler format
    const settings: Record<string, any> = {};
    
    Object.entries(data).forEach(([key, settingData]: [string, any]) => {
      settings[key] = settingData.value;
    });
    
    return settings;
  } catch (error) {
    console.error('Failed to load public settings:', error);
    // Return defaults
    return {
      ALLOW_PUBLIC_REGISTRATION: true,
      SITE_NAME: 'Risk Register',
      UI_PRIMARY_COLOR: '#1976d2',
      UI_SECONDARY_COLOR: '#dc004e',
    };
  }
}

/**
 * Check if public registration is allowed
 */
export async function getAllowPublicRegistration(): Promise<boolean> {
  try {
    const publicSettings = await getPublicSettings();
    return publicSettings.ALLOW_PUBLIC_REGISTRATION ?? true;
  } catch {
    // Default to true if we can't fetch settings
    return true;
  }
}